import { ArrowLeft, ArrowRight } from "components/icons/generated";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Icon from "ds/components/Icon";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import Card from "ds/components/Card";

import { StackFormContext } from "../context";
import useStackCreationAnalyticsVersion from "../useStackCreationAnalyticsVersion";
import NewStackIntermediateConfirmationStepTile from "./StepTile";
import { config } from "./constants";
import styles from "./styles.module.css";

const NewStackIntermediateConfirmation = () => {
  const urlParams = useURLParams();
  const hasCopiedConfiguration = urlParams.size !== 0;
  const analyticsVersion = useStackCreationAnalyticsVersion();

  const { setIsIntermediateStepActive, currentStep, goToNextStep, goToSummary, availableSteps } =
    useTypedContext(StackFormContext);

  const onContinue = () => {
    setIsIntermediateStepActive(false);
    goToNextStep();
  };

  const onBack = () => {
    setIsIntermediateStepActive(false);
  };

  const onSkipToSummary = () => {
    setIsIntermediateStepActive(false);
    goToSummary();
  };

  const currentStepIndex = availableSteps.findIndex(
    (availableStep) => availableStep.step === currentStep
  );

  const availableActions = config.filter(({ action: { step } }) => {
    const actionStepIndex = availableSteps.findIndex(
      (availableStep) => availableStep.step === step
    );

    if (actionStepIndex > currentStepIndex) {
      return true;
    }

    return false;
  });

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Stack created"
          description="Now you can make it even more powerful by adding hooks, attaching cloud, policies or contexts in the next steps!"
        />

        <Box direction="column" margin="0 0 large" gap="large">
          {hasCopiedConfiguration && (
            <Banner variant="warning" title="Configuration copied">
              Additional configuration was copied. You can go to summary to review it.
            </Banner>
          )}
          <Card
            variant="secondary"
            title="Choose shortcut or continue"
            titleTag="h3"
            contentGap="large"
          >
            {availableActions.map((action, i) => (
              <NewStackIntermediateConfirmationStepTile
                hasCopiedConfiguration={hasCopiedConfiguration}
                key={i}
                {...action}
              />
            ))}
          </Card>
        </Box>
      </FullScreenModalBody>
      <FullScreenModalFooter className={styles.footerWrapper} justify="end">
        <Box gap="medium" justify="end">
          <Button
            analyticsTitle="Skip to summary click"
            analyticsProps={{
              location: "Intermediate step",
              version: analyticsVersion,
            }}
            variant="secondary"
            onPress={onSkipToSummary}
          >
            Skip to summary
          </Button>
          <Button
            variant="secondary"
            analyticsPage={AnalyticsPageStack.StackNew}
            onPress={onBack}
            analyticsTitle="Back click"
            analyticsProps={{
              location: "Intermediate step",
              version: analyticsVersion,
            }}
            startIcon={({ className }) => <Icon src={ArrowLeft} className={className} />}
          >
            Back
          </Button>
          <Button
            variant="primary"
            analyticsTitle="Continue click on intermediate step"
            analyticsProps={{
              version: analyticsVersion,
            }}
            analyticsPage={AnalyticsPageStack.StackNew}
            onPress={onContinue}
            endIcon={({ className }) => <Icon src={ArrowRight} className={className} />}
          >
            Continue
          </Button>
        </Box>
      </FullScreenModalFooter>
    </>
  );
};

export default NewStackIntermediateConfirmation;
